import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Play } from 'lucide-react';
import { useT } from '../i18n';

/*
  ExitIntentBanner — when the cursor darts to the top of the window (towards
  the close button / tab bar), we make one polite, single-shot invitation:
  don't leave without seeing the live demo. Once per session, desktop only.
*/
export default function ExitIntentBanner() {
  const { lang } = useT();
  const es = lang === 'es';
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let armed = true;
    try { if (sessionStorage.getItem('kx_exit_banner') === '1') armed = false; } catch { /* noop */ }
    if (!armed || window.innerWidth < 768) return undefined;
    const cleanupRef = { fn: null };
    const arm = setTimeout(() => {
      const onOut = (e) => {
        if (e.relatedTarget || e.clientY > 8) return;
        try { sessionStorage.setItem('kx_exit_banner', '1'); } catch { /* noop */ }
        setOpen(true);
        document.removeEventListener('mouseout', onOut);
      };
      document.addEventListener('mouseout', onOut);
      cleanupRef.fn = () => document.removeEventListener('mouseout', onOut);
    }, 6000); // let them settle in first — never greet with a pop-up
    return () => { clearTimeout(arm); if (cleanupRef.fn) cleanupRef.fn(); };
  }, []);

  if (!open) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-[90] flex justify-center px-4 pointer-events-none" data-testid="exit-intent-banner"
      style={{ animation: 'kx-exit-drop 0.55s cubic-bezier(0.16,1,0.3,1) both' }}>
      <div className="pointer-events-auto mt-3 flex items-center gap-3 sm:gap-4 rounded-full border border-neutral-200 bg-white shadow-[0_18px_50px_-12px_rgba(0,0,0,0.28)] pl-4 pr-2 py-2">
        <span className="hidden sm:flex w-8 h-8 rounded-full bg-[#F77F11]/10 items-center justify-center shrink-0">
          <Play className="w-4 h-4 text-[#F77F11]" />
        </span>
        <p className="text-[13px] sm:text-[13.5px] text-neutral-800 font-medium">
          {es ? <>¡No te vayas todavía! Mira <b>tu restaurante con IA</b> en la demo en vivo — 2 minutos.</>
              : <>Don't leave just yet! See <b>your restaurant on AI</b> in the live demo — 2 minutes.</>}
        </p>
        <Link to="/demo" onClick={() => setOpen(false)} data-testid="exit-intent-demo-cta"
          className="shrink-0 rounded-full bg-[#F77F11] text-white text-[12.5px] font-bold px-4 py-2 hover:brightness-110 transition-[filter]">
          {es ? 'Ver la demo' : 'Watch the demo'}
        </Link>
        <button onClick={() => setOpen(false)} aria-label="Dismiss" data-testid="exit-intent-close"
          className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <style>{'@keyframes kx-exit-drop { 0% { opacity: 0; transform: translateY(-24px); } 100% { opacity: 1; transform: translateY(0); } }'}</style>
    </div>
  );
}
